import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { Mail, Lock, LogIn, AlertCircle, KeyRound, Sparkles, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatApiErrorDetail = (detail) => {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  if (detail && typeof detail.message === 'string') return detail.message;
  return String(detail);
};

export const Login = () => {
  const navigate = useNavigate();
  const { login, googleLogin, checkAuth } = useAuth();
  const [params] = useSearchParams();

  // Show idle-logout toast when redirected here with ?session=expired.
  // Deferred slightly so the Toaster portal has mounted first.
  useEffect(() => {
    if (params.get('session') === 'expired') {
      const t = setTimeout(() => {
        toast.info('You were logged out due to inactivity. Please log in again.', {
          duration: 6000,
          id: 'idle-logout-toast',
        });
      }, 120);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [params]);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Employee PIN
  const [pin, setPin] = useState('');
  const [pinLoading, setPinLoading] = useState(false);

  // Forgot password dialog
  const [fpOpen, setFpOpen] = useState(false);
  const [fpEmail, setFpEmail] = useState('');
  const [fpLoading, setFpLoading] = useState(false);

  // Reactivation request (shown when a deactivated employee is blocked)
  const [reOpen, setReOpen] = useState(false);
  const [reEmail, setReEmail] = useState('');
  const [reNote, setReNote] = useState('');
  const [reLoading, setReLoading] = useState(false);
  const [showReactivate, setShowReactivate] = useState(false);
  const [showResignup, setShowResignup] = useState(false);

  const isInactiveError = (status, detail) => status === 403 && /inactiv/i.test(String(detail || ''));

  const submitReactivation = async (e) => {
    e.preventDefault();
    if (!reEmail || !/.+@.+/.test(reEmail)) { toast.error('Enter your work email'); return; }
    setReLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/request-reactivation`, { email: reEmail, note: reNote });
      toast.success('Request sent. Your Admin/HR will review it shortly.');
      setReOpen(false); setReNote(''); setShowReactivate(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Something went wrong');
    } finally { setReLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const user = await login(email, password);
      toast.success('Welcome back!');
      if (user.role === 'admin') navigate('/admin');
      else if (user.role === 'employee') navigate('/employee');
      else navigate('/');
    } catch (err) {
      const rawDetail = err.response?.data?.detail;
      const code = rawDetail && typeof rawDetail === 'object' ? rawDetail.code : null;
      const detail = formatApiErrorDetail(rawDetail) || err.message;
      setError(detail);
      if (code === 'account_removed') {
        setShowResignup(true); setShowReactivate(false);
      } else if (isInactiveError(err.response?.status, detail)) {
        setShowReactivate(true); setReEmail(email); setShowResignup(false);
      } else {
        setShowReactivate(false); setShowResignup(false);
      }
    } finally { setLoading(false); }
  };

  const handlePinSubmit = async (e) => {
    e.preventDefault();
    if (pin.length !== 4) { toast.error('PIN must be exactly 4 digits'); return; }
    setPinLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/pin-login`, { pin }, { withCredentials: true });
      toast.success(`Welcome, ${res.data.name}`);
      setPin('');
      await checkAuth?.();
      navigate('/employee');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Invalid PIN';
      toast.error(detail);
      if (isInactiveError(err.response?.status, detail)) {
        setReEmail(''); setReNote(''); setReOpen(true);
      }
    } finally { setPinLoading(false); }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    if (!fpEmail) { toast.error('Enter your email'); return; }
    setFpLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email: fpEmail });
      toast.success("If an account exists for that email, we've sent a reset link.");
      setFpOpen(false); setFpEmail('');
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Something went wrong');
    } finally { setFpLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#FFFDF8]">
      <Header />

      <div className="container mx-auto px-4 py-10 max-w-5xl">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="text-center mb-8">
            <h1 className="text-4xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Sign in to Creative Birthdays
            </h1>
            <p className="text-[#6B6B7A] mt-2">Customers — email &amp; password · Employees — 4-digit PIN</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

            {/* ─── Customer Login ─── */}
            <div className="bg-white rounded-2xl border border-[#E8E5DF] p-8 shadow-sm" data-testid="customer-login-card">
              <h2 className="text-xl font-semibold text-[#1E1E24] mb-1" style={{ fontFamily: 'Fredoka' }}>Customer Sign In</h2>
              <p className="text-sm text-[#6B6B7A] mb-5">Access your bookings, invoices, and allergy forms.</p>

              {showResignup ? (
                <div className="mb-5 p-4 bg-[#FFF0F4] border border-[#FFC6D6] rounded-xl" data-testid="resignup-hint">
                  <div className="flex items-start gap-2">
                    <Sparkles className="w-4 h-4 text-[#FF3B6B] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-[#1E1E24]">Welcome back! Let's set up your account again.</p>
                      <p className="text-sm text-[#6B6B7A] mt-1">
                        {error || 'This account was removed. You can create a new account with this email to continue.'}
                      </p>
                      <Link
                        to={`/register${email ? `?email=${encodeURIComponent(email)}` : ''}`}
                        className="inline-flex items-center gap-1 mt-3 rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white text-sm font-semibold px-4 py-2"
                        data-testid="resignup-link"
                      >
                        <UserPlus className="w-4 h-4" /> Create a new account
                      </Link>
                    </div>
                  </div>
                </div>
              ) : error && (
                <div className="mb-5 p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2" data-testid="login-error">
                  <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-red-700">
                    <p>{error}</p>
                    {showReactivate && (
                      <button type="button" onClick={() => { setReEmail(email); setReOpen(true); }}
                        className="mt-2 text-[#FF3B6B] font-semibold hover:underline" data-testid="request-reactivation-link">
                        Request reactivation →
                      </button>
                    )}
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email" className="text-[#1E1E24] font-semibold">Email</Label>
                  <div className="relative mt-2">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B6B7A]" />
                    <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com" required data-testid="login-email"
                      className="pl-11 rounded-xl border-[#E8E5DF] focus:ring-[#FF3B6B] focus:border-[#FF3B6B]" />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-[#1E1E24] font-semibold">Password</Label>
                    <button type="button" onClick={() => { setFpEmail(email); setFpOpen(true); }}
                      className="text-xs text-[#FF3B6B] hover:underline" data-testid="forgot-password-link">
                      Forgot password?
                    </button>
                  </div>
                  <div className="relative mt-2">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B6B7A]" />
                    <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password" required data-testid="login-password"
                      className="pl-11 rounded-xl border-[#E8E5DF] focus:ring-[#FF3B6B] focus:border-[#FF3B6B]" />
                  </div>
                </div>

                <Button type="submit" disabled={loading} data-testid="login-submit"
                  className="w-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full py-5 btn-hover">
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Signing in…
                    </span>
                  ) : (<span className="flex items-center gap-2"><LogIn className="w-4 h-4" /> Sign In</span>)}
                </Button>
              </form>

              <div className="relative my-5">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[#E8E5DF]" /></div>
                <div className="relative flex justify-center text-xs">
                  <span className="px-3 bg-white text-[#6B6B7A]">or continue with</span>
                </div>
              </div>

              <Button type="button" variant="outline" onClick={googleLogin} data-testid="google-login"
                className="w-full rounded-full border-[#E8E5DF] hover:border-[#FF3B6B] py-5">
                <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                Continue with Google
              </Button>

              <p className="text-center text-[#6B6B7A] text-sm mt-5">
                Don't have an account?{' '}
                <Link to="/register" className="text-[#FF3B6B] font-semibold hover:underline">Sign up</Link>
              </p>
            </div>

            {/* ─── Employee PIN Login ─── */}
            <div className="bg-white rounded-2xl border border-[#E8E5DF] p-8 shadow-sm" data-testid="employee-pin-card">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#06D6A0]/10 flex items-center justify-center">
                  <KeyRound className="w-5 h-5 text-[#06D6A0]" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>Employee PIN Login</h2>
                  <p className="text-sm text-[#6B6B7A]">4-digit PIN · no email needed</p>
                </div>
              </div>

              <form onSubmit={handlePinSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="employee-pin" className="text-[#1E1E24] font-semibold">Your PIN</Label>
                  <Input
                    id="employee-pin"
                    type="password"
                    inputMode="numeric"
                    autoComplete="off"
                    value={pin}
                    onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    placeholder="••••"
                    maxLength={4}
                    className="mt-2 h-14 text-center text-2xl tracking-[0.6em] font-mono rounded-xl border-[#E8E5DF]"
                    data-testid="employee-pin-input"
                  />
                  <p className="text-xs text-[#6B6B7A] mt-1">Locked after 3 failed attempts in 10 minutes.</p>
                </div>

                <Button type="submit" disabled={pinLoading || pin.length !== 4} data-testid="employee-pin-submit"
                  className="w-full bg-[#06D6A0] hover:bg-[#05B88A] text-white rounded-full py-5">
                  {pinLoading ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Signing in…
                    </span>
                  ) : (<span className="flex items-center gap-2"><LogIn className="w-4 h-4" /> Employee Sign In</span>)}
                </Button>
              </form>

              <div className="mt-6 p-3 rounded-xl bg-[#FFFDF8] border border-[#E8E5DF] text-xs text-[#6B6B7A]">
                Forgot your PIN? Ask an admin to reset it from <span className="font-mono">/crm/employees</span>.
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Forgot password dialog */}
      <Dialog open={fpOpen} onOpenChange={setFpOpen}>
        <DialogContent className="max-w-md bg-white" data-testid="forgot-password-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Fredoka' }}>Reset your password</DialogTitle>
            <DialogDescription>Enter the email on your account and we'll send you a reset link (valid 1 hour).</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleForgotSubmit} className="space-y-4">
            <div>
              <Label htmlFor="fp-email" className="text-[#1E1E24] font-semibold">Email</Label>
              <Input id="fp-email" type="email" value={fpEmail} onChange={(e) => setFpEmail(e.target.value)}
                placeholder="you@example.com" required data-testid="fp-email"
                className="mt-2 rounded-xl border-[#E8E5DF]" />
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setFpOpen(false)} className="flex-1 rounded-full" disabled={fpLoading}>Cancel</Button>
              <Button type="submit" disabled={fpLoading} className="flex-1 rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white" data-testid="fp-submit">
                {fpLoading ? 'Sending…' : 'Send Reset Link'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      {/* Reactivation request dialog */}
      <Dialog open={reOpen} onOpenChange={setReOpen}>
        <DialogContent className="max-w-md bg-white" data-testid="reactivation-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Fredoka' }}>Request account reactivation</DialogTitle>
            <DialogDescription>Enter your work email and we'll send your Admin/HR a request to reactivate your access.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitReactivation} className="space-y-4">
            <div>
              <Label htmlFor="re-email" className="text-[#1E1E24] font-semibold">Work email</Label>
              <Input id="re-email" type="email" value={reEmail} onChange={(e) => setReEmail(e.target.value)}
                placeholder="you@work.com" required data-testid="reactivation-email"
                className="mt-2 rounded-xl border-[#E8E5DF]" />
            </div>
            <div>
              <Label htmlFor="re-note" className="text-[#1E1E24] font-semibold">Note <span className="font-normal text-[#6B6B7A]">(optional)</span></Label>
              <Input id="re-note" value={reNote} onChange={(e) => setReNote(e.target.value)}
                placeholder="Anything your manager should know" data-testid="reactivation-note"
                className="mt-2 rounded-xl border-[#E8E5DF]" />
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setReOpen(false)} className="flex-1 rounded-full" disabled={reLoading}>Cancel</Button>
              <Button type="submit" disabled={reLoading} className="flex-1 rounded-full bg-[#06D6A0] hover:bg-[#05B88A] text-white" data-testid="reactivation-submit">
                {reLoading ? 'Sending…' : 'Send Request'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Login;
